<script setup lang="ts">
import {
    faArrowUpArrowDown,
    faBus,
    faPlane,
    faPlaneDeparture,
    faTrain,
    faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { busSide, trainSide } from '@static/customIcons';
import RouteView from '@components/util/trip/TripCardSegment/RouteView.vue';
import { ReserveType } from '@utils/itinerary';
import { type searchHistoryItem } from '@search/types';

const router = useRouter();
const analyticsStore = useAnalyticsStore();
const searchFormStore = useSearchFormStore();

const routeIcons = {
    [ReserveType.Flight]: faPlane,
    [ReserveType.Train]: trainSide,
    [ReserveType.Bus]: busSide,
};
const icons = {
    [ReserveType.Bus]: faBus,
    [ReserveType.Flight]: faPlaneDeparture,
    [ReserveType.Train]: faTrain,
};

const formatDate = (date: string) => dayConfig.jalali(date).format('D MMMM');
const searchHistoryItemNormalizer = (searchHistories?: searchHistoryItem[]) =>
    searchHistories?.map(({ departureDate, returnDate, ...rest }) => ({
        ...rest,
        icon: icons?.[rest.reserveType as keyof typeof icons],
        departureDate: formatDate(departureDate),
        returnDate: returnDate ? formatDate(returnDate) : undefined,
    })) || [];

const isHomePage = computed(() => router.currentRoute.value.path === '/');

const searchHistoryItems = computed(() =>
    isHomePage.value
        ? searchHistoryItemNormalizer(
              Object.values(searchFormStore.allSearchHistory)
                  .flat()
                  .sort((a, b) =>
                      dayConfig.gregory(b.expireDate).isAfter(a.expireDate)
                          ? 1
                          : -1
                  )
          ).slice(0, 5)
        : searchHistoryItemNormalizer(searchFormStore?.searchHistory)
);
const searchHistoryIdentifier = computed(
    () => `${searchHistoryItems.value.length} مورد `
);

const cardClick = (item: searchHistoryItem, index: number) => {
    analyticsStore.trackAll('history_card_click', {
        card_index: index,
        number_of_cards: searchFormStore.searchHistory.length,
        service: searchFormStore.reserveType,
        page: isHomePage.value ? 'home' : 'landing',
        to: item.viewUrl,
        from: router.currentRoute.value.fullPath,
    });
    router.push(item.viewUrl);
};

const clearServiceSearchHistory = () => {
    analyticsStore.trackAll('history_clear_click', {
        page: isHomePage.value ? 'home' : 'landing',
    });
    if (isHomePage.value) {
        searchFormStore.clearAllServiceSearchHistory();
    }
    searchFormStore.clearServiceSearchHistory();
};

onMounted(() => {
    useAnalyticsStore().trackAll('history_section_impression', {
        number_of_cards: searchFormStore.searchHistory.length,
        service: searchFormStore.reserveType,
        page: isHomePage.value ? 'home' : 'landing',
    });
});
</script>

<template>
    <div v-if="searchHistoryItems.length" class="search-history">
        <div class="search-history-actions">
            <mr-typography
                :size="{
                    desktop: 'large',
                    mobile: 'medium',
                }"
                weight="700"
                color="var(--gray-900)"
            >
                جستجوهای اخیر
            </mr-typography>
            <mr-chips size="medium" color="var(--gray-200)">
                <mr-typography
                    size="small"
                    weight="500"
                    color="var(--gray-800)"
                >
                    {{ searchHistoryIdentifier }}
                </mr-typography>
            </mr-chips>
            <mr-Button
                class="search-history-clear"
                :left-icon="faTrash"
                variant="transparent"
                size="small"
                @click="clearServiceSearchHistory"
            >
                پاک کردن تاریخچه
            </mr-Button>
        </div>
        <div class="cards-wrapper">
            <div
                v-for="(item, index) in searchHistoryItems"
                :key="index"
                class="search-history-card"
                @click="cardClick(item, index)"
            >
                <div class="search-history-info">
                    <mr-Icon :icon="item.icon" />
                    <mr-typography
                        weight="700"
                        color="vae(--cool-gray-900)"
                        class="location"
                        white-space="nowrap"
                    >
                        {{ item.originName }}
                    </mr-typography>
                    <RouteView
                        :icon="
                            routeIcons?.[item.reserveType as keyof typeof icons]
                        "
                        color="var(--cool-gray-500)"
                        :rotate="
                            item.reserveType === ReserveType.Flight
                                ? 180
                                : undefined
                        "
                    />
                    <mr-typography
                        weight="700"
                        color="var(--cool-gray-900)"
                        class="location"
                        white-space="nowrap"
                    >
                        {{ item.destinationName }}
                    </mr-typography>
                </div>
                <div class="search-history-dates">
                    <mr-typography
                        size="small"
                        weight="500"
                        color="var(--cool-gray-700)"
                    >
                        {{ item.departureDate }}
                    </mr-typography>
                    <template v-if="item.returnDate">
                        <mr-icon
                            :icon="faArrowUpArrowDown"
                            rotation="270"
                            size="small"
                            color="var(--cool-gray-500)"
                        />
                        <mr-typography
                            size="small"
                            weight="500"
                            color="var(--cool-gray-700)"
                        >
                            {{ item.returnDate }}
                        </mr-typography>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.search-history {
    padding: 20px 16px 0px 0px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    max-width: 1200px;
    margin: 0 auto;
    @include desktop-only {
        padding-right: 0;
    }
    .search-history-actions {
        display: flex;
        align-items: center;
        gap: 12px;
        .search-history-clear {
            margin-right: auto;
        }
    }
    /* Hide scrollbar for Chrome, Safari and Opera */
    .cards-wrapper::-webkit-scrollbar {
        display: none;
    }
    .cards-wrapper {
        width: 100%;
        display: flex;
        overflow-x: scroll;
        gap: 12px;
        -ms-overflow-style: none; /* IE and Edge */
        scrollbar-width: none; /* Firefox */
        .search-history-card {
            display: flex;
            flex-direction: column;
            padding: 6px 8px;
            gap: 4px;
            border-radius: 12px;
            min-width: 220px !important;
            flex-shrink: 0;
            height: 72px;
            margin: 2px 1px;
            @include shadow();
            @include desktop-only {
                min-width: 227.1px;
            }
            .search-history-info {
                width: 100%;
                display: flex;
                align-items: center;
                gap: 8px;
                padding-left: 8px;
                height: 32px;
                .location {
                    max-width: 67px;
                    overflow-x: hidden;
                    text-overflow: ellipsis;
                    flex-shrink: 0;
                }
            }
            .search-history-dates {
                display: flex;
                align-items: center;
            }
        }
        .search-history-card:last-child {
            margin-left: 16px;
            @include desktop-only {
                margin-left: 0;
            }
        }
    }
}
</style>
